import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import CheckIcon from '@material-ui/icons/Check'
import RefinersToolbar from '@components/coaches-search/CoachResults/Refiners/RefinersToolbar'
import { POPULAR_GOAL_CHIPS } from '@components/coaches-search/CoachResults/PopularGoals'
import {
  ChevronDown,
  Flag,
  COACH_PROFILE_FILTER_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import NearLocationInput from './near-location-input'
import { useStyles } from '../CoachesSearch.styles'

const ALL_RESULTS_OPTION = { id: null, label: 'Show all coaches' }
const FOCUS_PLACEHOLDER = 'Focus'

function PersonaFilterMenu({
  anchorEl,
  onClose,
  options,
  selectedId,
  onSelect,
  paperClassName,
}) {
  const classes = useStyles()
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      getContentAnchorEl={null}
      PaperProps={{
        className: [classes.filterMenuPaper, paperClassName].filter(Boolean).join(' '),
      }}
    >
      {options.map((option) => {
        const selected = selectedId === option.id
        return (
          <MenuItem
            key={option.id || '__all_results__'}
            selected={selected}
            onClick={() => {
              onSelect(option.id)
              onClose()
            }}
            className={classes.filterMenuItem}
            classes={{ selected: classes.filterMenuItemSelected }}
          >
            <ListItemIcon className={classes.filterMenuLeading}>
              {selected ? <CheckIcon fontSize="small" /> : <span className={classes.filterMenuLeadingSpacer} aria-hidden />}
            </ListItemIcon>
            <Typography component="span" variant="body2">{option.label}</Typography>
          </MenuItem>
        )
      })}
    </Menu>
  )
}

PersonaFilterMenu.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
  })).isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  paperClassName: PropTypes.string,
}

function DirectoryHeaderPanel({
  placeName,
  selectedPersonaId,
  onPersonaChange,
  onLocationSelect,
  savedOnly,
  onToggleSavedOnly,
  savedCoachCount,
  refinerData,
  onClearDirectoryFilters,
  bookThisWeek,
  onToggleBookThisWeek,
}) {
  const classes = useStyles()
  const [personaAnchor, setPersonaAnchor] = useState(null)
  const personaOptions = [ALL_RESULTS_OPTION, ...POPULAR_GOAL_CHIPS]
  const selectedPersona = personaOptions.find((option) => option.id === selectedPersonaId)
    || ALL_RESULTS_OPTION
  const focusLabel = selectedPersonaId ? selectedPersona.label : FOCUS_PLACEHOLDER
  const handleClearPersona = useCallback(() => {
    onPersonaChange(null, selectedPersonaId)
  }, [onPersonaChange, selectedPersonaId])

  return (
    <Box className={classes.filtersLine} data-testid="directory-filters-line">
      <Box className={classes.splitSearchBar} data-testid="directory-split-search">
        {onPersonaChange && (
          <>
            <Button
              type="button"
              disableElevation
              disableRipple
              className={[
                classes.splitFocusTrigger,
                Boolean(personaAnchor) && classes.splitFocusTriggerOpen,
                selectedPersonaId && classes.splitFocusTriggerActive,
              ].filter(Boolean).join(' ')}
              aria-haspopup="true"
              aria-expanded={Boolean(personaAnchor)}
              aria-label={`Focus filter, ${selectedPersona.label} selected`}
              onClick={(event) => setPersonaAnchor(event.currentTarget)}
            >
              <Flag
                {...COACH_PROFILE_FILTER_ICON_PROPS}
                size={20}
                className={classes.splitFocusIcon}
              />
              <span className={classes.splitFocusValue}>{focusLabel}</span>
              <ChevronDown
                {...COACH_PROFILE_FILTER_ICON_PROPS}
                size={18}
                className={[
                  classes.contextDropdownCaret,
                  personaAnchor && classes.contextDropdownCaretOpen,
                ].filter(Boolean).join(' ')}
              />
            </Button>
            <PersonaFilterMenu
              anchorEl={personaAnchor}
              onClose={() => setPersonaAnchor(null)}
              options={personaOptions}
              selectedId={selectedPersonaId}
              onSelect={(personaId) => onPersonaChange(personaId, selectedPersonaId)}
            />
            <Divider
              orientation="vertical"
              flexItem
              className={classes.splitSearchDivider}
            />
          </>
        )}
        <NearLocationInput
          defaultPlaceName={placeName}
          onLocationSelect={onLocationSelect}
        />
      </Box>

      <Box className={classes.filtersLineMenus}>
        <RefinersToolbar
          refinerData={refinerData}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          savedCoachCount={savedCoachCount}
          selectedPersonaId={selectedPersonaId}
          bookThisWeek={bookThisWeek}
          onToggleBookThisWeek={onToggleBookThisWeek}
          onClearDirectoryFilters={onClearDirectoryFilters}
          onClearFilters={onPersonaChange ? handleClearPersona : undefined}
        />
      </Box>
    </Box>
  )
}

DirectoryHeaderPanel.propTypes = {
  placeName: PropTypes.string,
  selectedPersonaId: PropTypes.string,
  onPersonaChange: PropTypes.func,
  onLocationSelect: PropTypes.func,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func,
  savedCoachCount: PropTypes.number,
  refinerData: PropTypes.object,
  onClearDirectoryFilters: PropTypes.func,
  bookThisWeek: PropTypes.bool,
  onToggleBookThisWeek: PropTypes.func,
}

export default DirectoryHeaderPanel
